import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

if (!process.env.REACT_APP_BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required');
const API = `${process.env.REACT_APP_BACKEND_URL}/api/profile/score-preferences`;
const EVENT = 'parseeker-score-preferences';

export const useScoreAnimationPreference = userId => {
  const [state, setState] = useState({ userId, enabled: false, ready: false, saving: false, error: '' });
  const current = useRef(userId);
  current.current = userId;
  const lock = useRef(false);
  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await axios.get(API);
      if (typeof data.score_animations_enabled !== 'boolean') throw new Error('Invalid preference response');
      if (current.current === userId) setState({ userId, enabled: data.score_animations_enabled, ready: true, saving: false, error: '' });
    } catch {
      if (current.current === userId) setState({ userId, enabled: false, ready: false, saving: false, error: 'Could not load score animation preference.' });
    }
  }, [userId]);
  useEffect(() => {
    current.current = userId;
    setState({ userId, enabled: false, ready: false, saving: false, error: '' });
    load();
    const changed = event => {
      if (event.detail?.userId === userId) setState({ userId, enabled: event.detail.enabled, ready: true, saving: false, error: '' });
    };
    window.addEventListener(EVENT, changed);
    return () => { current.current = null; window.removeEventListener(EVENT, changed); };
  }, [userId, load]);
  const update = async enabled => {
    if (!userId || lock.current || !state.ready) return;
    lock.current = true;
    setState(previous => ({ ...previous, saving: true, error: '' }));
    try {
      const { data } = await axios.put(API, { score_animations_enabled: enabled });
      if (typeof data.score_animations_enabled !== 'boolean') throw new Error('Invalid preference response');
      if (current.current === userId) window.dispatchEvent(new CustomEvent(EVENT, { detail: { userId, enabled: data.score_animations_enabled } }));
    } catch {
      if (current.current === userId) setState(previous => ({ ...previous, saving: false, error: 'Could not save this setting. Please try again.' }));
    } finally { lock.current = false; }
  };
  return { ...state, enabled: state.userId === userId && state.ready && state.enabled, update, reload: load };
};